import { Form } from "@inertiajs/react"
import { useState } from "react"

import InputError from "@/components/input-error"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import type { PermissionCatalog } from "@/types"

interface RoleFormValues {
  id?: number
  name?: string
  description?: string | null
  system?: boolean
  permissions?: string[]
}

interface Props {
  action: string
  method: "post" | "patch"
  availablePermissions: PermissionCatalog
  role?: RoleFormValues
  submitLabel: string
}

export function RoleForm({
  action,
  method,
  availablePermissions,
  role,
  submitLabel,
}: Props) {
  // Held in state rather than left to the DOM so the per-category counts and
  // the group toggles have something to read.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(role?.permissions ?? []),
  )

  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleGroup = (keys: string[], on: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      keys.forEach((key) => (on ? next.add(key) : next.delete(key)))
      return next
    })
  }

  const categoryKeys = (groups: Record<string, Record<string, string>>) =>
    Object.values(groups).flatMap((group) => Object.keys(group))

  return (
    <Form method={method} action={action} className="space-y-6">
      {({ errors, processing }) => (
        <>
          {/* The matrix lives in React state, so the selection is submitted as
              hidden inputs rather than as the checkboxes themselves. */}
          {[...selected].map((key) => (
            <input
              key={key}
              type="hidden"
              name="role[permissions][]"
              value={key}
            />
          ))}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="role[name]"
                  defaultValue={role?.name}
                  placeholder="owner"
                  disabled={role?.system}
                />
                {role?.system && (
                  <p className="text-muted-foreground text-xs">
                    Built-in roles can&apos;t be renamed — the app refers to
                    this one by name.
                  </p>
                )}
                <InputError messages={errors.name} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="role[description]"
                  defaultValue={role?.description ?? ""}
                  rows={2}
                  placeholder="What this role is for."
                />
                <InputError messages={errors.description} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Permissions</CardTitle>
                <Badge variant="secondary">{selected.size} selected</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              <InputError messages={errors.permissions} />

              {Object.entries(availablePermissions).map(
                ([category, groups]) => {
                  const keys = categoryKeys(groups)
                  const chosen = keys.filter((key) => selected.has(key)).length

                  return (
                    <section key={category} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold">{category}</h3>
                          <span className="text-muted-foreground text-xs">
                            {chosen}/{keys.length}
                          </span>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            toggleGroup(keys, chosen < keys.length)
                          }
                        >
                          {chosen < keys.length ? "Select all" : "Clear"}
                        </Button>
                      </div>

                      <div className="grid gap-6 sm:grid-cols-2">
                        {Object.entries(groups).map(([group, permissions]) => (
                          <div key={group} className="space-y-2">
                            <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                              {group}
                            </p>
                            {Object.entries(permissions).map(([key, label]) => (
                              <div key={key} className="flex items-start gap-2">
                                <Checkbox
                                  id={key}
                                  checked={selected.has(key)}
                                  onCheckedChange={() => toggle(key)}
                                />
                                <Label
                                  htmlFor={key}
                                  className="text-sm leading-tight font-normal"
                                >
                                  {label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>

                      <Separator />
                    </section>
                  )
                },
              )}
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {submitLabel}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={() => history.back()}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </Form>
  )
}
